/**
 * SessionChatView - Virtualized chat message list component
 * 
 * Features:
 * - Uses @tanstack/react-virtual for efficient rendering of large message lists
 * - Scroll-to-bottom button for navigation
 * - Message count indicator
 * - Empty state handling
 * - Accessible with role="log"
 * - Exposes scrollToBottom via ref for parent control
 */
import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown } from 'lucide-react'
import { SessionMessage as SessionMessageComponent } from './SessionMessage'
import { Button } from '@/components/ui/button'
import type { SessionMessage } from '@/lib/types'

interface SessionChatViewProps {
  messages: SessionMessage[]
  /** Force scroll to bottom on initial load or when messages change significantly */
  scrollToBottomOnLoad?: boolean
  /** Session ID - when this changes, scroll resets */
  sessionId?: string
}

export interface SessionChatViewRef {
  scrollToBottom: () => void
}

export const SessionChatView = forwardRef<SessionChatViewRef, SessionChatViewProps>(function SessionChatView(
  { messages, scrollToBottomOnLoad = false, sessionId },
  ref
) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const initialScrollDoneRef = useRef(false)
  const prevSessionIdRef = useRef(sessionId)
  const messagesLengthRef = useRef(messages.length)
  
  // Keep messages length in sync
  messagesLengthRef.current = messages.length

  // Virtualizer for efficient rendering of large lists
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120, // Estimated row height
    overscan: 5, // Number of items to render outside of viewport
  })
  
  // Store virtualizer in ref for stable access
  const virtualizerRef = useRef(virtualizer)
  virtualizerRef.current = virtualizer

  // Check if scrolled away from bottom
  const handleScroll = useCallback(() => {
    const container = parentRef.current
    if (!container) return
    
    const { scrollTop, scrollHeight, clientHeight } = container
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
    setShowScrollButton(!isAtBottom && messages.length > 0)
  }, [messages.length])

  // Scroll to bottom using virtualizer for virtualized content
  const scrollToBottom = useCallback(() => {
    const len = messagesLengthRef.current
    if (len > 0) {
      // Note: 'smooth' is not supported with dynamic-sized items in react-virtual
      virtualizerRef.current.scrollToIndex(len - 1, { align: 'end', behavior: 'auto' })
    }
  }, [])

  // Expose scrollToBottom to parent via ref
  useImperativeHandle(ref, () => ({
    scrollToBottom
  }), [scrollToBottom])

  // Auto-scroll to bottom when new messages arrive. Keyed on the newest message as well as the count:
  // a paged transcript slides — a new message in, the oldest out — so its length stops changing.
  const newestMessage = messages[messages.length - 1]
  useEffect(() => {
    if (parentRef.current && messages.length > 0) {
      // Check if we're near the bottom before auto-scrolling
      const container = parentRef.current
      const { scrollTop, scrollHeight, clientHeight } = container
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 200
      
      if (isNearBottom) {
        scrollToBottom()
      }
    }
  }, [messages.length, newestMessage, scrollToBottom])

  // Force scroll to bottom on initial load when scrollToBottomOnLoad is true
  useEffect(() => {
    if (scrollToBottomOnLoad && messages.length > 0 && !initialScrollDoneRef.current) {
      // Use a slightly longer delay to ensure virtualizer has measured content
      const timer = setTimeout(() => {
        // Use instant scroll for initial load (no smooth animation)
        virtualizer.scrollToIndex(messages.length - 1, { align: 'end', behavior: 'auto' })
        initialScrollDoneRef.current = true
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [scrollToBottomOnLoad, messages.length, virtualizer])

  // Reset initial scroll flag when session changes
  useEffect(() => {
    if (sessionId !== prevSessionIdRef.current) {
      initialScrollDoneRef.current = false
      prevSessionIdRef.current = sessionId
    }
  }, [sessionId])

  // Empty state
  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <p>No messages yet</p>
      </div>
    )
  }

  const messageCount = messages.length
  const messageLabel = messageCount === 1 ? '1 message' : `${messageCount} messages`

  return (
    <div className="flex flex-col h-full">
      {/* Message count indicator */}
      <div className="px-4 py-2 text-sm text-muted-foreground border-b">
        {messageLabel}
      </div>

      {/* Virtualized message list */}
      <div
        ref={parentRef}
        data-scroll-container
        data-virtualized="true"
        role="log"
        aria-label={`Chat messages, ${messageLabel}`}
        className="flex-1 overflow-auto relative"
        onScroll={handleScroll}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const message = messages[virtualItem.index]
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <div className="p-2">
                  <SessionMessageComponent message={message} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute bottom-4 right-4 rounded-full shadow-lg"
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
})
